/* Darchi & Co: fresh, mixed discounted products on every homepage visit.
   Scoped enhancement: no product/catalog changes and no global gallery changes. */
(function () {
  'use strict';
  const HISTORY_KEY = 'darchi-savings-cycle-v3';
  const LEGACY_HISTORY_KEY = 'darchi-savings-visits-v2';
  const GROUP_TARGETS = { furniture: 10, glassware: 4, tableware: 4, lighting: 2 };

  function shuffle(items, random) {
    const result = items.slice();
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  function validOffers(offers) {
    const unique = new Map();
    for (const offer of Array.isArray(offers) ? offers : []) {
      if (!offer || !/^\d+$/.test(String(offer.id)) || !/^\d+$/.test(String(offer.variant))) continue;
      if (!(Number(offer.price) > 0 && Number(offer.compare) > Number(offer.price))) continue;
      if (typeof offer.url !== 'string' || !offer.url.startsWith('/') || offer.url.startsWith('//')) continue;
      if (!offer.title || !Array.isArray(offer.photos) || !offer.photos.length) continue;
      if (typeof offer.photos[0].src !== 'string' || !/^(https:\/\/|\/\/|\/)/.test(offer.photos[0].src)) continue;
      const percent = Math.floor((Number(offer.compare) - Number(offer.price)) * 100 / Number(offer.compare));
      if (percent < 1) continue;
      const item = Object.assign({}, offer, { id: String(offer.id), variant: String(offer.variant), percent: percent });
      if (!unique.has(item.id)) unique.set(item.id, item);
    }
    return Array.from(unique.values());
  }

  function ids(values) {
    return Array.from(new Set((Array.isArray(values) ? values : []).map(String).filter(function (id) { return /^\d+$/.test(id); })));
  }

  function normalizeState(value) {
    if (Array.isArray(value)) {
      const visits = value.filter(Array.isArray);
      return { version: 3, cycle: 0, seen: ids(visits.flat()), previous: ids(visits[0]), avoid: [] };
    }
    const state = value && typeof value === 'object' ? value : {};
    return {
      version: 3,
      cycle: Number.isSafeInteger(state.cycle) && state.cycle >= 0 ? state.cycle : 0,
      seen: ids(state.seen),
      previous: ids(state.previous),
      avoid: ids(state.avoid)
    };
  }

  function mergeState(current, stored) {
    const first = normalizeState(current);
    const second = normalizeState(stored);
    const cycle = Math.max(first.cycle, second.cycle);
    return {
      version: 3,
      cycle: cycle,
      seen: ids((first.cycle === cycle ? first.seen : []).concat(second.cycle === cycle ? second.seen : [])),
      previous: first.previous.length ? first.previous : second.previous,
      avoid: ids(first.previous.concat(second.previous))
    };
  }

  function mixedOffers(candidates, limit, random) {
    const ordered = shuffle(candidates, random);
    const chosen = [];
    const used = new Set();
    function take(group, target) {
      let added = 0;
      for (const offer of ordered) {
        if (chosen.length >= limit || added >= target) break;
        if (used.has(offer.id) || (group && offer.group !== group)) continue;
        chosen.push(offer);
        used.add(offer.id);
        added += 1;
      }
    }
    for (const group of Object.keys(GROUP_TARGETS)) take(group, GROUP_TARGETS[group]);
    take(null, limit);
    // Category variety applies only to these candidates, never by repeating a
    // scarce category that has already been shown in the current catalog pass.
    const groups = shuffle(Array.from(new Set(chosen.map(function (offer) { return offer.group; }))), random);
    const shuffled = shuffle(chosen, random);
    const lead = groups.map(function (group) { return shuffled.find(function (offer) { return offer.group === group; }); });
    const leadIds = new Set(lead.map(function (offer) { return offer.id; }));
    return lead.concat(shuffled.filter(function (offer) { return !leadIds.has(offer.id); }));
  }

  function selectCycleOffers(offers, history, count, random) {
    const pool = validOffers(offers);
    const limit = Math.min(Math.max(1, Math.floor(Number(count) || 20)), pool.length);
    const state = normalizeState(history);
    const available = new Set(pool.map(function (offer) { return offer.id; }));
    let seen = new Set(state.seen.filter(function (id) { return available.has(id); }));
    const previous = new Set(state.previous.filter(function (id) { return available.has(id); }));
    const combinedPrevious = new Set(state.previous.concat(state.avoid).filter(function (id) { return available.has(id); }));
    let cycle = state.cycle;
    const rng = random || Math.random;
    if (!limit) return { offers: [], state: { version: 3, cycle: cycle, seen: [], previous: [] } };
    if (seen.size === pool.length) { cycle += 1; seen = new Set(); }
    // The current page's last full grid has priority. Also avoid the other tab's
    // most recent grid when enough alternatives remain, without weakening that
    // local guarantee on a smaller catalog.
    const blocked = pool.length - combinedPrevious.size >= limit ? combinedPrevious : pool.length - previous.size >= limit ? previous : new Set();
    const eligible = pool.filter(function (offer) { return !blocked.has(offer.id); });
    const unseen = eligible.filter(function (offer) { return !seen.has(offer.id); });
    if (unseen.length < limit) {
      // An older tab or inconsistent saved state can say its currently visible
      // products are the only items missing from the newer pass. They are
      // already displayed, so account for them before closing that pass rather
      // than silently recycling seen items inside an unfinished pass.
      blocked.forEach(function (id) { seen.add(id); });
    }
    const chosen = mixedOffers(unseen, limit, rng);
    const chosenIds = new Set(chosen.map(function (offer) { return offer.id; }));
    chosenIds.forEach(function (id) { seen.add(id); });
    // Close the pass before drawing any repeat. At a partial final visit, only
    // the recycled fill belongs to the next pass; the final unseen items closed
    // the previous one. Keeping fill after unseen also preserves that order.
    if (seen.size === pool.length) { cycle += 1; seen = new Set(); }
    if (chosen.length < limit) {
      const fillPool = eligible.filter(function (offer) { return !chosenIds.has(offer.id); });
      const fill = mixedOffers(fillPool, limit - chosen.length, rng);
      fill.forEach(function (offer) { chosen.push(offer); seen.add(offer.id); });
    }
    return {
      offers: chosen,
      state: { version: 3, cycle: cycle, seen: Array.from(seen), previous: chosen.map(function (offer) { return offer.id; }) }
    };
  }

  function selectOffers(offers, history, count, random) {
    return selectCycleOffers(offers, history, count, random).offers;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { shuffle: shuffle, validOffers: validOffers, selectOffers: selectOffers, selectCycleOffers: selectCycleOffers, mergeState: mergeState };
  if (typeof document === 'undefined') return;

  let memoryState = null;
  let visitNumber = 0;
  function readStoredState() {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || 'null');
      if (stored && stored.version === 3 && Array.isArray(stored.seen) && Array.isArray(stored.previous)) return normalizeState(stored);
    } catch (error) { /* Try migrating the older history if the cycle is absent or corrupt. */ }
    try {
      return normalizeState(JSON.parse(localStorage.getItem(LEGACY_HISTORY_KEY) || '[]'));
    } catch (error) { /* Storage can be blocked; randomness still works. */ }
    return normalizeState(null);
  }
  function readHistory() {
    // This page's last displayed products remain the immediate previous visit,
    // even if another tab changes storage or persistence is unavailable.
    return mergeState(memoryState, readStoredState());
  }
  function remember(nextState) {
    // Re-read immediately before writing so another tab's completed visit is
    // merged rather than overwritten. The local page keeps its actual last set.
    const latest = readStoredState();
    memoryState = mergeState(nextState, latest);
    if (latest.cycle > nextState.cycle) memoryState.seen = ids(memoryState.seen.concat(nextState.previous));
    const saved = { version: 3, cycle: memoryState.cycle, seen: memoryState.seen, previous: memoryState.previous };
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(saved)); } catch (error) { /* Keep the in-memory fallback. */ }
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }
  function customElement(tag, className) {
    // Create custom elements inertly. Dawn's product-form constructor requires
    // its complete form subtree to exist when the element is upgraded.
    const template = document.createElement('template');
    template.innerHTML = '<' + tag + '></' + tag + '>';
    const node = template.content.firstElementChild;
    if (className) node.className = className;
    return node;
  }
  function label(template, product, number) {
    return String(template || '').replace(/__PRODUCT__/g, function () { return product; }).replace(/__NUMBER__/g, String(number));
  }
  function link(className, text, href) {
    const node = element('a', className, text);
    node.href = href;
    return node;
  }
  function hidden(name, value) {
    const input = element('input');
    input.type = 'hidden'; input.name = name; input.value = value;
    return input;
  }
  function image(photo, title) {
    const img = element('img', 'darchi-card-gallery__image');
    img.src = photo.src;
    img.alt = photo.alt || title;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.draggable = false;
    if (Number(photo.w) > 0) img.width = Number(photo.w);
    if (Number(photo.h) > 0) img.height = Number(photo.h);
    img.sizes = '(min-width: 1200px) 17vw, (min-width: 750px) 30vw, 46vw';
    try {
      const src = new URL(photo.src, document.baseURI);
      const widths = [180, 360, 540, 720].filter(function (width) { return !photo.w || width <= photo.w; });
      if (widths.length) img.srcset = widths.map(function (width) {
        const resized = new URL(src.href); resized.searchParams.set('width', String(width));
        return resized.href + ' ' + width + 'w';
      }).join(', ');
    } catch (error) { /* The original image URL remains usable. */ }
    return img;
  }
  function card(offer, config) {
    const labels = config.labels;
    const article = element('article', 'wwv-home__product wwv-home__product--sale');
    article.dataset.darchiOffer = '';
    article.dataset.productId = offer.id;
    article.dataset.variantId = offer.variant;
    article.dataset.savingPercent = String(offer.percent);
    article.dataset.savingsGroup = offer.group;
    const gallery = customElement('darchi-card-gallery', 'darchi-card-gallery' + (offer.id === '15403081433178' ? ' darchi-card-gallery--contain' : ''));
    gallery.setAttribute('role', 'group');
    gallery.setAttribute('aria-label', label(labels.gallery, offer.title, 1));
    const track = element('div', 'darchi-card-gallery__track');
    offer.photos.forEach(function (photo, index) {
      const slide = link('darchi-card-gallery__slide', null, offer.url);
      slide.setAttribute('aria-label', label(labels.photo, offer.title, index + 1));
      slide.tabIndex = index === 0 ? 0 : -1;
      slide.appendChild(image(photo, offer.title));
      track.appendChild(slide);
    });
    gallery.appendChild(track);
    if (offer.photos.length > 1) {
      const controls = element('div', 'darchi-card-gallery__controls');
      const previous = element('button'); previous.type = 'button';
      previous.dataset.photoDirection = '-1'; previous.setAttribute('aria-label', labels.previous);
      const prevGlyph = element('span', '', '\u2039'); prevGlyph.setAttribute('aria-hidden', 'true'); previous.appendChild(prevGlyph);
      const counter = element('span', 'darchi-card-gallery__count'); counter.setAttribute('aria-live', 'polite');
      const current = element('span', '', '1'); current.dataset.photoIndex = '';
      counter.append(current, document.createTextNode(' / ' + offer.photos.length));
      const next = element('button'); next.type = 'button';
      next.dataset.photoDirection = '1'; next.setAttribute('aria-label', labels.next);
      const nextGlyph = element('span', '', '\u203a'); nextGlyph.setAttribute('aria-hidden', 'true'); next.appendChild(nextGlyph);
      controls.append(previous, counter, next); gallery.appendChild(controls);
    }
    const badge = element('span', 'wwv-home__sale-badge', String(labels.save).replace(/__PERCENT__/g, String(offer.percent)));
    const heading = element('h3', 'wwv-home__product-title');
    heading.appendChild(link('', offer.title, offer.url));
    const variant = offer.variantTitle && offer.variantTitle !== 'Default Title' ? element('p', 'wwv-home__sale-variant', offer.variantTitle) : null;
    const price = element('div', 'wwv-home__price wwv-home__sale-prices');
    price.append(element('strong', '', offer.priceText), element('del', '', offer.compareText));
    const actions = element('div', 'darchi-card-actions');
    if (offer.sellingPlan || Number(offer.variantCount) > 1) {
      actions.appendChild(link('darchi-card-action button button--secondary', labels.choose, offer.url));
    } else {
      const productForm = customElement('product-form');
      productForm.dataset.sectionId = config.sectionId;
      const form = element('form', 'darchi-card-action-form');
      form.method = 'post'; form.action = config.cartAddUrl;
      form.id = 'wwv-savings-' + config.sectionId + '-' + offer.id + '-' + visitNumber;
      form.setAttribute('accept-charset', 'UTF-8');
      form.enctype = 'multipart/form-data'; form.noValidate = true;
      form.dataset.type = 'add-to-cart-form';
      const variantInput = hidden('id', offer.variant);
      variantInput.className = 'product-variant-id';
      form.append(hidden('form_type', 'product'), hidden('utf8', '\u2713'), variantInput);
      const button = element('button', 'darchi-card-action button button--secondary');
      button.type = 'submit'; button.name = 'add';
      button.append(element('span', '', labels.add), element('span', 'sold-out-message hidden', labels.soldOut));
      const spinner = document.getElementById('darchi-savings-spinner');
      if (spinner) button.appendChild(spinner.content.cloneNode(true));
      form.appendChild(button); productForm.appendChild(form); actions.appendChild(productForm);
    }
    article.append(badge, gallery, heading);
    if (variant) article.appendChild(variant);
    article.append(price, actions);
    return article;
  }

  function refresh() {
    const data = document.getElementById('darchi-savings-catalog');
    const oldGrid = document.querySelector('#shopify-section-main-homepage #selected-best-sellers [data-darchi-savings-grid]') ||
      document.querySelector('#shopify-section-main-homepage #selected-best-sellers .wwv-home__products');
    if (!data || !oldGrid) return false;
    try {
      const config = JSON.parse(data.textContent);
      const history = readHistory();
      const selection = selectCycleOffers(config.offers, history, config.count, Math.random);
      const offers = selection.offers;
      if (!offers.length) return false;
      visitNumber += 1;
      const grid = oldGrid.cloneNode(false);
      grid.dataset.darchiSavingsGrid = '';
      grid.dataset.darchiSavingsVersion = String(config.version);
      grid.dataset.darchiSavingsBuild = '5';
      const fragment = document.createDocumentFragment();
      offers.forEach(function (offer) { fragment.appendChild(card(offer, config)); });
      grid.appendChild(fragment);
      // Replace the node itself, not just its children. The legacy hourly script
      // retains its detached grid and can no longer reinsert its fixed products.
      oldGrid.replaceWith(grid);
      remember(selection.state);
      return true;
    } catch (error) {
      console.warn('Darchi savings: retaining the server-rendered offers.', error);
      return false;
    }
  }
  let ready = document.readyState !== 'loading';
  let initialized = false;
  let returnPending = document.visibilityState === 'hidden';
  let returnHandled = false;
  function markAway() {
    returnPending = true;
    returnHandled = false;
  }
  function enterPage() {
    if (!ready || document.visibilityState === 'hidden' || (initialized && !returnPending)) return;
    const isReturn = initialized || returnPending;
    if (refresh()) {
      initialized = true;
      returnPending = false;
      if (isReturn) returnHandled = true;
    }
  }
  if (!ready) document.addEventListener('DOMContentLoaded', function () {
    ready = true;
    enterPage();
  }, { once: true });
  else enterPage();
  window.addEventListener('pagehide', markAway);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') markAway();
    else enterPage();
  });
  // Visibility and pageshow can arrive in either order on a cache restore.
  // A completed return consumes both signals; focus only retries a pending one.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && !returnHandled) returnPending = true;
    enterPage();
  });
  window.addEventListener('focus', function () { if (returnPending) enterPage(); });
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.id === 'shopify-section-main-homepage') {
      returnPending = true;
      enterPage();
    }
  });
})();




